"use client";

/**
 * "Not seeing what you want?" — a term request from inside the library.
 *
 * The 404 already turns a missed concept URL into a request. This is the same
 * loop for the far more common case: someone browsing, not finding it, and
 * leaving without ever hitting a 404. That person is invisible to the existing
 * capture and is exactly who the backlog wants to hear from.
 *
 * ## A term or a whole area
 *
 * The field accepts both, and deliberately does not make the visitor choose.
 * "Board meetings" is a topic area; "SAFE" is a term; a founder does not think
 * in that distinction and should not be asked to. Classification is a judgement
 * made by the human reading the digest, who has the corpus in front of them and
 * can see whether an ask is one page or eight. Forcing the choice up front would
 * push the decision onto the person least equipped to make it, and would add a
 * form control to a box whose entire value is being one line long.
 *
 * Shares the whole pipeline with `LearnMissCapture` deliberately — the same
 * abuse screen, the same `learn_term_requested` event, the same promise of
 * review rather than publication. One dataset with one set of admission rules
 * is worth more than two that drifted.
 *
 * The promise wording is policy, not decoration: pages auto-generated from
 * search misses are what search engines demote as thin programmatic content,
 * and publishing them would put the reviewed corpus at risk by association.
 * Never soften "we will consider" into "we will add".
 *
 * ## One person, several gaps
 *
 * The form resets rather than ending the conversation. Someone who has just
 * discovered the library is the most likely person to know three things missing
 * from it, and a terminal thank-you state throws away the second and third for
 * no reason.
 *
 * Repeats of the same normalised term from one visitor are recorded once per
 * visit, the same rule `LearnSearch` applies to keystroke misses. The digest
 * ranks by how many *people* asked, so one person pressing the button twice
 * must not read as two votes.
 *
 * That dedupe is held in `sessionStorage`, not in a ref, and the distinction is
 * load-bearing now that the box renders on all 155 concept pages rather than
 * only the index. A ref lives for one mount; a visitor moving between concepts
 * remounts the component and would be counted again on every page. `LearnSearch`
 * can still use a ref honestly because it exists only on the index, where one
 * mount is one visit.
 *
 * ## Why the address is asked for AFTER the suggestion
 *
 * The box promises "we will let you know when we publish it", which needs an
 * address, and the obvious place for one is a second field on the form. It is
 * the wrong place. This form's entire value is being one line long, and a
 * two-field form asks every visitor for a commitment before they have made the
 * cheap one. So the address is asked for on the confirmation panel instead:
 * only someone who has already suggested something sees it, the ask is framed
 * as a benefit rather than a toll, and skipping it costs them nothing — the
 * suggestion has already landed.
 *
 * It travels under its own intent (`notifyMeAboutTerm`), which the server
 * treats as attach-only. That is what stops a second submission from reading
 * as a second vote in the dataset that exists to count people.
 */
import Link from "next/link";
import { useEffect, useRef, useState } from "react";

import {
  screenTermProposal,
  type LearnTermMissSurface,
} from "@/lib/learn/termProposal";
import {
  notifyMeAboutTerm,
  notifyTermRequest,
} from "@/lib/learn/termRequestClient";
import { SS_LEARN_REQUESTED_TERMS } from "@/lib/storage-keys";

/**
 * Terms already recorded from this visitor, read from and written to
 * `sessionStorage`.
 *
 * A `useRef` set was enough while the box lived only on the index, because one
 * mount covered the whole visit. On 155 concept pages it is not: navigating
 * from one concept to the next remounts the component, the set starts empty,
 * and the same person asking for the same term twice reads as two votes in the
 * exact dataset that exists to count people (PR #578 review).
 *
 * Storage is best-effort by design. Safari's private mode throws on access and
 * a visitor may have storage disabled entirely; in both cases the correct
 * behaviour is to record the request rather than to drop it. A duplicate vote
 * is a much smaller problem than a lost demand signal, so every failure path
 * here falls open.
 */
function readRecordedTerms(): Set<string> {
  try {
    const stored = window.sessionStorage.getItem(SS_LEARN_REQUESTED_TERMS);
    if (!stored) return new Set();
    const parsed: unknown = JSON.parse(stored);
    return Array.isArray(parsed)
      ? new Set(parsed.filter((item): item is string => typeof item === "string"))
      : new Set();
  } catch {
    // benign: sessionStorage is unavailable (Safari private mode, storage
    // disabled). Falling open records the request rather than dropping it —
    // see the docblock above.
    return new Set();
  }
}

function rememberRecordedTerm(term: string) {
  try {
    const terms = readRecordedTerms();
    terms.add(term);
    window.sessionStorage.setItem(
      SS_LEARN_REQUESTED_TERMS,
      JSON.stringify([...terms]),
    );
  } catch {
    // benign: sessionStorage is unavailable; falls open, see the docblock above.
  }
}

/**
 * Where the box is rendered. Narrower than `LearnTermMissSurface` on purpose:
 * this component can only ever produce a deliberate request, never a 404 or a
 * chat miss, so those surfaces must not be passable here. `Exclude` rather
 * than an allowlist because the exclusion IS the invariant — a new render
 * surface belongs here by default unless it shares the same reason to be
 * barred (simplify pass, PR #596).
 */
export type LearnSuggestSurface = Exclude<
  LearnTermMissSurface,
  "learn-not-found" | "levai-chat"
>;

/**
 * The warm panel every state of this box sits in — the form and both terminal
 * states.
 *
 * A constant rather than three copies of the class string because the three
 * states are the same object at different moments; the reader watches one panel
 * change its contents, and a border that shifts weight between them reads as a
 * flicker. Padding stays per-state: the form is taller than a sentence.
 *
 * Warm rather than white on purpose. Everything above this box on a concept
 * page is something to read; this one asks for an action, so it matches the
 * "Start with your idea" CTA below it instead of the content cards above.
 */
const SUGGEST_PANEL =
  "rounded-2xl border border-lev-orange/40 bg-lev-orange-50/60";

/**
 * Which way the box's own contents are justified.
 *
 * Not cosmetic, and deliberately not derived from `surface` — that would couple
 * layout to analytics. The index is a centred page (centred hero, centred
 * search) so a centred box belongs there. A concept page is a left-aligned
 * reading column where every block shares one left and one right edge
 * (`LEARN_MEASURE`); a centred island inside it reads as a mistake, because the
 * column is offset from the page centre and the box's centre therefore lands
 * beside the centre of the conversion band directly below it.
 */
export type LearnSuggestAlign = "center" | "start";

export function LearnSuggestTerm({
  surface,
  align = "center",
  captureEmail = false,
}: {
  surface: LearnSuggestSurface;
  align?: LearnSuggestAlign;
  /**
   * Whether to offer the "tell me when it publishes" field on the confirmation
   * panel. Resolved server-side from `LEARN_REQUEST_CAPTURE` and passed down,
   * because this is a client component on a statically prerendered page: there
   * is no user to evaluate a per-user flag against, and reading the env var
   * here would not survive the bundle. Defaults to `false` so a render site
   * that has not been wired up promises nothing it cannot keep.
   */
  captureEmail?: boolean;
}) {
  const [term, setTerm] = useState("");
  const [done, setDone] = useState(false);
  /**
   * The screened term this visitor just suggested, held so the follow-up
   * address can be attached to the right row after the input has been cleared.
   * Null when the last submission recorded nothing (a duplicate in this
   * session, or a screened-out term) — in which case there is no row to attach
   * to and the field must not be offered.
   */
  const [requestedTerm, setRequestedTerm] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  /**
   * Set when the visitor asked for something the corpus already has. Not a
   * request and never recorded as one: `TermProposalVerdict` states the intent
   * on the `known` branch itself — "Already in the corpus — send them to the
   * page rather than queueing it." Answering "a human reviews every
   * suggestion" to someone who typed a term we already explain is the worst of
   * both, since it withholds the answer AND implies work that will not happen.
   */
  const [known, setKnown] = useState<{ slug: string; term: string } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  /**
   * True only when the form returns after a submission, so focus moves back to
   * the input rather than being dropped at document start when the button the
   * visitor just pressed unmounts. Not on first mount: stealing focus from a
   * reader arriving on a concept page would be worse than the problem.
   */
  const returnedToFormRef = useRef(false);

  // Full class names, never constructed — Tailwind scans source text, so a
  // built-up string like `text-${align}` would be purged out of the bundle.
  const isCentered = align === "center";
  const blockAlign = isCentered ? "text-center" : "text-left";
  /**
   * The copy measure, applied only when the box is centred.
   *
   * A centred paragraph needs its own cap or it runs the full width of a wide
   * card as one long line. Left-aligned inside the concept page's reading
   * column, the column already IS the measure (`LEARN_MEASURE`), so a second
   * cap here just wraps the copy early and strands empty space against the
   * card's right edge — the paragraph stops well short of the box it sits in.
   */
  const copyMeasure = isCentered ? "mx-auto max-w-[52ch]" : "";

  const showingForm = !done && !known;

  useEffect(() => {
    if (!showingForm || !returnedToFormRef.current) return;
    returnedToFormRef.current = false;
    inputRef.current?.focus();
  }, [showingForm]);

  /** Back to the empty form, with focus, from either terminal state. */
  const askAgain = () => {
    returnedToFormRef.current = true;
    setTerm("");
    setDone(false);
    setKnown(null);
    // The follow-up belongs to the suggestion that has just been dismissed. A
    // second suggestion gets its own offer, and a stale `emailSent` would
    // silently withhold it.
    setRequestedTerm(null);
    setEmail("");
    setEmailSent(false);
  };

  if (known) {
    return (
      <div
        role="status"
        className={`${SUGGEST_PANEL} px-6 py-5 ${blockAlign}`}
      >
        <p
          className={`${copyMeasure} text-[15px] leading-[1.6] text-landing-dark`}
        >
          Good news — we have written that one already.
        </p>
        <Link
          href={`/learn/${known.slug}`}
          className="mt-3 inline-block text-[15px] font-semibold text-lev-orange hover:underline"
        >
          Read {known.term} →
        </Link>
        <div>
          <button
            type="button"
            onClick={askAgain}
            className="mt-3 text-[14px] font-semibold text-landing-secondary hover:underline"
          >
            Ask for a different one
          </button>
        </div>
      </div>
    );
  }

  if (done) {
    // Offered only when there is a row to attach to AND the deployment is
    // capturing addresses. Both halves matter: without the first the address
    // would have nowhere to go, and without the second we would be promising a
    // notification the build cannot send.
    const canOfferNotify = captureEmail && requestedTerm !== null && !emailSent;

    return (
      <div
        role="status"
        className={`${SUGGEST_PANEL} px-6 py-5 ${blockAlign}`}
      >
        <p
          className={`${copyMeasure} text-[15px] leading-[1.6] text-landing-dark`}
        >
          Noted, thank you. A human reviews every suggestion and decides what
          gets written next, so we cannot promise this one will appear. Enough
          people asking is genuinely how the list gets ordered.
        </p>

        {canOfferNotify && (
          <form
            className={`${isCentered ? "mx-auto max-w-[440px]" : ""} mt-4 flex flex-col gap-2.5 sm:flex-row`}
            onSubmit={(event) => {
              event.preventDefault();
              const trimmed = email.trim();
              if (trimmed.length === 0) return;
              notifyMeAboutTerm(requestedTerm, trimmed);
              setEmailSent(true);
            }}
          >
            <label htmlFor="learn-suggest-email" className="sr-only">
              Your email, to hear if we write it
            </label>
            <input
              id="learn-suggest-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@company.com"
              className="w-full rounded-full border border-slate-300 bg-white px-5 py-2.5 text-[15px] text-landing-dark placeholder:text-landing-secondary/70 focus:border-lev-orange focus:outline-none focus:ring-2 focus:ring-lev-orange/25"
            />
            <button
              type="submit"
              disabled={email.trim().length === 0}
              className="shrink-0 rounded-full bg-landing-dark px-5 py-2.5 text-[14px] font-semibold text-white transition-colors hover:bg-landing-dark/90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Tell me
            </button>
          </form>
        )}
        {canOfferNotify && (
          <p
            className={`${copyMeasure} mt-2 text-[13px] leading-[1.5] text-landing-secondary`}
          >
            Optional. If we write it, we will email you once to say so, and use
            your address for nothing else.
          </p>
        )}

        {emailSent && (
          <p
            className={`${copyMeasure} mt-4 text-[15px] leading-[1.6] text-landing-dark`}
          >
            We will email you once, if it gets written.
          </p>
        )}

        <button
          type="button"
          onClick={askAgain}
          className="mt-4 text-[14px] font-semibold text-lev-orange hover:underline"
        >
          Suggest another
        </button>
      </div>
    );
  }

  return (
    <div className={`${SUGGEST_PANEL} px-6 py-6 ${blockAlign}`}>
      <h2 className="mb-1.5 text-[18px] font-semibold tracking-[-0.2px] text-landing-dark">
        Not seeing what you need?
      </h2>
      <p
        className={`${copyMeasure} mb-5 text-[15px] leading-[1.6] text-landing-secondary`}
      >
        A single term or a whole area we have not covered yet. Both are useful,
        and what founders ask for is how we decide what to write next.
      </p>
      <form
        /* The 440px cap keeps a centred row from becoming a wide lonely band.
           Left-aligned it does the opposite — it strands empty space to the
           right of the input — so the field fills the measure instead, matching
           the full-width search input on the index. */
        className={`${isCentered ? "mx-auto max-w-[440px]" : ""} flex flex-col gap-2.5 sm:flex-row`}
        onSubmit={(event) => {
          event.preventDefault();
          const verdict = screenTermProposal(term);
          // Already written: answer the question instead of queueing it. This
          // branch exists because the verdict type asks for it by name, and
          // because "we will consider it" is a strange reply to a term that is
          // one click away.
          if (verdict.status === "known") {
            setKnown({ slug: verdict.slug, term: verdict.normalized });
            return;
          }
          // Rejected terms get the same acknowledgement and record nothing.
          // Telling someone their input was screened is how a gate gets probed,
          // and an honest "thanks, a human will look" is true either way: a
          // human decides what gets written, including nothing.
          if (
            verdict.status === "candidate" &&
            !readRecordedTerms().has(verdict.normalized)
          ) {
            rememberRecordedTerm(verdict.normalized);
            // One call: the server both emails and emits the
            // `learn_term_requested` event (the route is callable outside the
            // UI, so the event lives at that boundary, not here).
            notifyTermRequest(verdict.normalized, surface);
            setRequestedTerm(verdict.normalized);
          }
          setDone(true);
        }}
      >
        <label htmlFor="learn-suggest" className="sr-only">
          Suggest a concept
        </label>
        <input
          id="learn-suggest"
          ref={inputRef}
          type="text"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="A term or topic: “board meetings”, “SAFEs”…"
          className="w-full rounded-full border border-slate-300 bg-white px-5 py-2.5 text-[15px] text-landing-dark placeholder:text-landing-secondary/70 focus:border-lev-orange focus:outline-none focus:ring-2 focus:ring-lev-orange/25"
        />
        <button
          type="submit"
          disabled={term.trim().length === 0}
          /* Dark, not orange, even though the panel around it is warm: white on
             `lev-orange` is ~3.2:1, under the 4.5:1 WCAG AA floor for 14px
             text, and these are public indexed pages. Dark-on-cream is both
             accessible and the stronger CTA against an orange panel. */
          className="shrink-0 rounded-full bg-landing-dark px-5 py-2.5 text-[14px] font-semibold text-white transition-colors hover:bg-landing-dark/90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Suggest it
        </button>
      </form>
    </div>
  );
}
